import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface PlaceGalleryProps {
  images: string[];
  alt: string;
}

export function PlaceGallery({ images, alt }: PlaceGalleryProps) {
  const photos = images.filter(Boolean);
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const count = photos.length;
  const go = (i: number) => setIndex(((i % count) + count) % count);
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  // touch swipe
  const touchStart = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStart.current = null;
  };

  // keyboard in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, index]);

  if (count === 0) return null;

  return (
    <>
      <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[4/3] md:aspect-auto md:h-[420px] group">
        <div
          ref={trackRef}
          className="size-full flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {photos.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(true)}
              className="relative shrink-0 w-full h-full block cursor-zoom-in"
              aria-label={`open photo ${i + 1}`}
            >
              <img
                src={src}
                alt={i === 0 ? alt : `${alt} — photo ${i + 1}`}
                className="size-full object-cover"
                draggable={false}
                loading={i === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="previous photo"
              className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 size-9 sm:size-10 rounded-full bg-white/90 hover:bg-white text-navy shadow-md inline-flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="next photo"
              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 size-9 sm:size-10 rounded-full bg-white/90 hover:bg-white text-navy shadow-md inline-flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition"
            >
              <ChevronRight className="size-5" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/35 backdrop-blur">
              {photos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`go to photo ${i + 1}`}
                  className={`rounded-full transition-all ${
                    i === index ? "w-5 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/60 hover:bg-white/90"
                  }`}
                />
              ))}
            </div>

            <span className="absolute top-3 right-3 text-[11px] font-medium px-2 py-1 rounded-full bg-black/45 text-white tabular-nums">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>

      {lightbox && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center animate-reveal"
          style={{ zIndex: 2147483600 }}
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            aria-label="close"
            className="absolute top-4 right-4 size-11 rounded-full bg-white/15 hover:bg-white/30 text-white inline-flex items-center justify-center"
            style={{ zIndex: 2147483647 }}
          >
            <X className="size-5" />
          </button>

          <div
            className="relative w-full h-full flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={photos[index]}
              alt={`${alt} — photo ${index + 1}`}
              className="max-w-[95vw] max-h-[88vh] object-contain select-none"
              draggable={false}
            />
            {count > 1 && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="previous photo"
                  className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 size-11 rounded-full bg-white/10 hover:bg-white/25 text-white inline-flex items-center justify-center"
                >
                  <ChevronLeft className="size-6" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="next photo"
                  className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 size-11 rounded-full bg-white/10 hover:bg-white/25 text-white inline-flex items-center justify-center"
                >
                  <ChevronRight className="size-6" />
                </button>
                <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white text-xs tabular-nums px-3 py-1 rounded-full bg-white/10">
                  {index + 1} / {count}
                </span>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
